import { useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  ArrowLeftIcon,
  InboxArrowDownIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { routes } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { vendors } from "@sablier/v2-utils";
import { useRouter } from "next/router";
import { useAccount, useExpected, useModalExpected } from "~/client/hooks";

const Wrapper = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
  }

  ${(props) => props.theme.medias.maxMD} {
    column-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
  }

  ${(props) => props.theme.medias.maxSM} {
    & > *:first-child {
      display: none;
    }
  }
`;

function Navigation() {
  const router = useRouter();

  const { isConfigured } = useAccount();
  const { doOpen: doOpenExpected } = useModalExpected();
  const { configured: list } = useExpected();
  const { t } = useT();

  const isExpected = useMemo(
    () => isConfigured && list.length !== 0,
    [isConfigured, list],
  );

  const openExpected = useCallback(() => {
    vendors.track.log((events) => events.openModal("expected"));
    doOpenExpected();
  }, [doOpenExpected]);

  const Specific = useCallback(() => {
    switch (router.pathname) {
      case routes.client.pages.dashboard.route:
        return (
          <>
            <Button
              accent={"dark200"}
              appearance={"solid"}
              left={PlusIcon}
              purpose={"internal"}
              to={routes.client.pages.gallery.builder()}
              tooltip={t("pages.createSingle")}
              onClick={() => vendors.track.log("clickCreateStreamShortcut")}
            />
          </>
        );

      case routes.client.pages.createGroup.route:
        return (
          <Button
            accent={"dark200"}
            appearance={"solid"}
            left={ArrowLeftIcon}
            title={t("pages.gallery")}
            purpose={"internal"}
            to={routes.client.pages.galleryGroup.builder()}
            tooltip={t("structs.goBack")}
          />
        );
      case routes.client.pages.createAirstream.route:
      case routes.client.pages.createSingle.route:
        return (
          <Button
            accent={"dark200"}
            appearance={"solid"}
            left={ArrowLeftIcon}
            title={t("pages.gallery")}
            purpose={"internal"}
            titleMedium={t("words.back")}
            to={routes.client.pages.gallery.builder()}
            tooltip={t("structs.goBack")}
          />
        );
      case routes.client.pages.gallery.route:
      case routes.client.pages.galleryGroup.route:
        return (
          <Button
            accent={"dark200"}
            appearance={"solid"}
            left={ArrowLeftIcon}
            title={t("pages.dashboard")}
            titleMedium={t("words.back")}
            purpose={"internal"}
            to={routes.client.pages.dashboard.builder()}
            tooltip={t("structs.goBack")}
          />
        );
      default:
        return <></>;
    }
  }, [router, t]);

  const Expected = useCallback(() => {
    if (isExpected) {
      return (
        <Button
          accent={"red"}
          appearance={"gradient"}
          left={InboxArrowDownIcon}
          onClick={openExpected}
          isDisabled={!isConfigured}
          purpose={"button"}
          tooltip={t("structs.expectedStreams")}
        />
      );
    }
    return (
      <Button
        accent={"dark200"}
        appearance={"solid"}
        left={InboxArrowDownIcon}
        onClick={openExpected}
        isDisabled={!isConfigured}
        purpose={"button"}
        tooltip={t("structs.expectedStreams")}
      />
    );
  }, [openExpected, isConfigured, isExpected, t]);
  return (
    <Wrapper>
      <Specific />
      <Expected />
    </Wrapper>
  );
}

export default Navigation;
